export const RACI_COLUMNS = Object.freeze([
  { key: 'R', label: 'Responsible' },
  { key: 'A', label: 'Accountable' },
  { key: 'C', label: 'Consulted' },
  { key: 'I', label: 'Informed' },
]);

const DEFAULT_STAKEHOLDERS = Object.freeze([
  {
    id: 'stakeholder-product-owner',
    type: 'person',
    name: 'Product owner',
    organization: 'Program delivery office',
    role: 'Product direction and acceptance',
    domain: 'Product',
    raci: ['A', 'C'],
  },
  {
    id: 'stakeholder-pm',
    type: 'person',
    name: 'Project manager',
    organization: 'Execution team',
    role: 'Daily delivery, due-outs, and gate readiness',
    domain: 'Delivery',
    raci: ['R', 'I'],
  },
  {
    id: 'stakeholder-engineering',
    type: 'organization',
    name: 'Engineering support cell',
    organization: 'Technical delivery',
    role: 'Architecture, code review, and integration risk',
    domain: 'Engineering',
    raci: ['R', 'C'],
  },
  {
    id: 'stakeholder-contracting',
    type: 'organization',
    name: 'Contracting support',
    organization: 'Acquisition and finance',
    role: 'Option timing, renewal signals, and funding coordination',
    domain: 'Contracting',
    raci: ['C', 'I'],
  },
]);

const DEFAULT_CRITICAL_DATES = Object.freeze([
  {
    id: 'critical-gate',
    label: 'Next gate decision',
    date: '2026-05-20',
    owner: 'Project manager',
    status: 'Needs attention',
    source: 'Lifecycle gate',
  },
  {
    id: 'critical-review',
    label: 'Annual product review',
    date: '2026-06-30',
    owner: 'Product owner',
    status: 'Upcoming',
    source: 'Product governance',
  },
  {
    id: 'critical-license',
    label: 'License true-up window',
    date: '2026-07-15',
    owner: 'Resource manager',
    status: 'On track',
    source: 'Licensing register',
  },
]);

const PROJECT_SIGNAL_OVERRIDES = Object.freeze({
  'PRJ-9924-A': {
    stakeholders: [
      {
        id: 'sg-pm',
        type: 'person',
        name: 'Maya Chen',
        organization: 'Sentinel Guard PMO',
        role: 'Project manager and closeout owner',
        domain: 'Delivery',
        raci: ['R', 'I'],
      },
      {
        id: 'sg-pll',
        type: 'person',
        name: 'Robin Russell',
        organization: 'Portfolio leadership layer',
        role: 'Gate approval and timeline exception review',
        domain: 'Leadership',
        raci: ['A', 'C'],
      },
      {
        id: 'sg-cyber',
        type: 'organization',
        name: 'DHA Cyber Integration Office',
        organization: 'Cyber assurance',
        role: 'ATO package, control evidence, and risk acceptance',
        domain: 'Cyber',
        raci: ['R', 'C'],
      },
      {
        id: 'sg-contracting',
        type: 'organization',
        name: 'PEO DHMS Contracting Cell',
        organization: 'Acquisition support',
        role: 'Option renewal, deliverable timing, and funding coordination',
        domain: 'Contracting',
        raci: ['C', 'I'],
      },
      {
        id: 'sg-data',
        type: 'organization',
        name: 'EIDS Data Steward Team',
        organization: 'Data governance',
        role: 'Source lineage, data quality, and evidence traceability',
        domain: 'Data',
        raci: ['C', 'I'],
      },
    ],
    criticalDates: [
      {
        id: 'sg-gate-3',
        label: 'Gate 3 decision forum',
        date: '2026-05-20',
        owner: 'Robin Russell',
        status: 'Needs attention',
        source: 'Lifecycle gate',
      },
      {
        id: 'sg-contract-option',
        label: 'Contract option renewal notice',
        date: '2026-06-14',
        owner: 'PEO DHMS Contracting Cell',
        status: 'Upcoming',
        source: 'Contracting register',
      },
      {
        id: 'sg-code-review',
        label: 'Quarterly secure code review',
        date: '2026-06-21',
        owner: 'DHA Cyber Integration Office',
        status: 'On track',
        source: 'Cyber review cadence',
      },
      {
        id: 'sg-license',
        label: 'License renewal checkpoint',
        date: '2026-07-08',
        owner: 'Resource manager',
        status: 'On track',
        source: 'Licensing register',
      },
    ],
  },
});

export function getProjectDemoSignals(project = {}) {
  const override = PROJECT_SIGNAL_OVERRIDES[project.id] || {};
  return {
    projectId: project.id || 'project',
    projectName: project.name || 'Project',
    stakeholders: override.stakeholders || DEFAULT_STAKEHOLDERS,
    criticalDates: override.criticalDates || DEFAULT_CRITICAL_DATES,
  };
}

export function criticalDateTone(status) {
  const value = String(status || '').toLowerCase();
  if (/overdue|late|block/.test(value)) return 'brick';
  if (/attention|risk|watch/.test(value)) return 'amber';
  if (/upcoming|planned/.test(value)) return 'blue';
  return 'sage';
}
